// useAddAppointment.js
import { useState, useEffect } from "react";
import { getAllServicesGroup } from "../../api/servicesGroupApi";
import { getbarbers, getAvailableTimeSlots } from "../../api/barberApi";
import { getAllCustomer } from "../../api/customerApi";
import { AddAppointment } from "../../api/appointmentApi";
import { useAuth } from "../../context/AuthContext";

const useAddAppointment = () => {
  const [staffId, setStaffId] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedTime, setSelectedTime] = useState("");
  const [additionalMinutes, setAdditionalMinutes] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [serviceList, setServiceList] = useState([]);
  const [availableServiceList, setAvailableServiceList] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [availableTimes, setAvailableTimes] = useState(null);
  const [message, setMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timeSlotList, setTimeSlotList] = useState([]);

  const { user } = useAuth();
  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getAllCustomer(user.barberShop);
        setCustomers(response);
        setSelectedCustomer(response[0]);
        var bar = await getbarbers(user.barberShop);
        let barberlist = [];
        if (bar) {
          barberlist = bar.map((b) => ({
            id: b.id,
            name: b.name,
          }));
        }
        var services = await getAllServicesGroup(user.barberShop);
        setLoading(false);
        setBarbers(barberlist);
        setStaffId(barberlist[0]?.id || "");
        setServiceList(services);
      } catch (err) {
        setMessage(err.message);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    setSelectedServices([]);
    setSelectedTime("");
    setTimeSlotList([]);
    if (staffId) {
      const filteredCategories = serviceList.map((category) => ({
        ...category,
        services: category.services
          .map((service) => ({
            ...service,
            barberServices: service.barberServices.filter(
              (bs) =>
                bs.barberId &&
                staffId &&
                bs.barberId.toUpperCase() === staffId.toUpperCase(),
            ),
          }))
          .filter((service) => service.barberServices.length > 0),
      }));
      setAvailableServiceList(filteredCategories);
    } else {
      setAvailableServiceList([]);
    }
  }, [staffId]);

  useEffect(() => {
    var totalTime = 0;
    var totalPrice = 0;
    selectedServices.forEach((s) => {
      availableServiceList.forEach((group) => {
        // Find the service in the current group
        const service = group.services.find(
          (service) => service.id === s.serviceId,
        );
        if (service) {
          totalTime += service.barberServices[0].time * s.quantity;
          totalPrice += service.price * s.quantity;
        }
      });
    });
    setTotalPrice(totalPrice);
    setTotalTime(totalTime);
  }, [selectedServices]);
  // Check availability whenever barber, date, time, or serviceList changes
  const fetchAvailability = async () => {
    if (!staffId || !date || selectedServices.length === 0) return;

    try {
      const result = await getAvailableTimeSlots(
        staffId,
        user.barberShop,
        date,
        totalTime,
      );
      setTimeSlotList(result);
    } catch (err) {
      setMessage(err.message);
    }
  };
  useEffect(() => {
    fetchAvailability();
  }, [date, totalTime]);

  const handleServiceChange = (serviceId) => {
    setSelectedServices((prev) => {
      const existing = prev.find((s) => s.serviceId === serviceId);
      if (existing) {
        return prev.filter((s) => s.serviceId !== serviceId);
      } else {
        return [...prev, { serviceId, quantity: 1 }];
      }
    });
  };
  const handleServiceQuantityChange = (serviceId, quantity) => {
    setSelectedServices((prev) =>
      prev.map((s) => (s.serviceId === serviceId ? { ...s, quantity } : s)),
    );
  };
  const validateForm = () => {
    if (!user?.barberShop) return "Barbershop is required";
    if (!staffId) return "Please select a staff member";
    if (!selectedCustomer?.id) return "Please select a customer";
    if (!selectedServices.length) return "Select at least one service";
    if (!date) return "Select a date";
    if (!selectedTime) return "Select a time";
    if (additionalMinutes < 0) return "Invalid additional minutes";

    for (const s of selectedServices) {
      if (!s.serviceId) return "Invalid service";
      if (!s.quantity || s.quantity <= 0) return "Quantity must be > 0";
    }

    return null;
  };
  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      setMessage({ text: error, type: "danger" });
      setShowMessage(true);
      return;
    }
    try {
      // Example list of services with dynamic GUID keys
      const selectedServiceList = selectedServices.map((s) => ({
        id: s.serviceId,
        quantity: s.quantity,
      }));

      // Convert to dictionary-style object expected by .NET
      const servicesDict = {};
      selectedServiceList.forEach((s) => {
        servicesDict[s.id] = s.quantity;
      });

      const response = await AddAppointment(
        user.barberShop,
        staffId,
        selectedCustomer.id,
        servicesDict,
        date,
        selectedTime,
        additionalMinutes,
      );
      if (response.data.isSuccess) {
        setMessage({ text: response.data.value, type: "success" });
        setShowMessage(true);
      } else {
        setMessage({
          text: response?.data.errors?.join("\n"),
          type: "danger",
        });
        setShowMessage(true);
      }
    } catch (err) {
      console.error("Error fetching appointments:", err);
    }
  };

  return {
    staffId,
    setStaffId,
    selectedCustomer,
    setSelectedCustomer,
    barbers,
    customers,
    date,
    setDate,
    selectedTime,
    setSelectedTime,
    additionalMinutes,
    setAdditionalMinutes,
    handleServiceChange,
    handleServiceQuantityChange,
    selectedServices,
    handleSubmit,
    message,
    showMessage,
    setShowMessage,
    loading,
    totalPrice,
    totalTime,
    timeSlotList,
    availableServiceList,
  };
};

export default useAddAppointment;
